import React, { useEffect } from 'react';
import {
    InstantSearch,
} from 'react-instantsearch-dom';
import { graphql, useStaticQuery, navigate } from 'gatsby';
import FilterInput from '../../components/primitives/filter-input';
import Row from '../../components/primitives/grid/row';
import Col from '../../components/primitives/grid/col';
import SearchBackground from '../../images/search-bg.png';
import Container from '../../components/primitives/grid/container';
// import { StaticImage } from 'gatsby-plugin-image';
import Tabs, { Tab, TabList, TabPanel, TabPanels } from '../../components/primitives/tabs';
import useSearchBuilder, {
    HiddenSearchBox,
    useSearchTermState,
} from '../../utils/use-search-builder';
import useSearchPrefix from '../../utils/use-search-prefix';
import Pagination from '../../components/primitives/pagination';
import { SearchTileHits } from '../../components/directory/search-tile';
import { SearchTilePressHits } from '../../components/directory/search-tile-press';
import { SearchTileFacilityHits } from '../../components/directory/search-tile-facility';
import { Persons } from '../../components/blocks/persons/persons-filter-card';

const SearchPage = (): JSX.Element => {
    const [searchTerm, setSearchTerm] = useSearchTermState();
    const algoliaProps = useSearchBuilder();
    const searchPrefix = useSearchPrefix();

    const {
        wp: {
            siteOptions: {
                websiteFunktionen: {
                    enableContact,
                    enableContactPerson,
                    enableEmergency,
                    enableEvents,
                    enableJobs,
                    enableLocations,
                    enableSitePlan,
                    enableBlogPosts,
                },
            },
        },
    } = useStaticQuery(graphql`
        query {
          wp {
            siteOptions {
              websiteFunktionen {
                enableContact
                enableContactPerson
                enableEmergency
                enableEvents
                enableJobs
                enableLocations
                enableSitePlan
                enableBlogPosts
              }
            }
          }
        }
      `);

    return (
        <div css={{ width: '100%', overflowX: 'hidden' }}>
            <div css={{ position: 'relative', background: '#F6F6F6', overflow: 'hidden' }}>
                <div css={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                }}
                >
                    <img
                        src={SearchBackground}
                        alt="background"
                        css={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                </div>
                <Container css={{
                    padding: '105px 0',
                    '@media (max-width: 767px)': {
                        padding: '48px 22px',
                    },
                }}
                >
                    <Row>
                        <Col breakpoints={{ dt: { start: 3, span: 6 }, mb: { span: 4 } }}>
                            <FilterInput name="search" placeholder='Suche' value={searchTerm} onChange={setSearchTerm} />
                        </Col>
                    </Row>
                </Container>
            </div>
            <Tabs defaultIndex={0} css={{ overflowX: 'hidden' }}>
                <TabList
                    css={{
                        overflowX: 'auto',
                        '@media (max-width: 767px)': {
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                        },
                    }}
                >
                    <Tab
                        value="webseiten"
                        css={{
                            '@media (max-width: 767px)': {
                                width: '100%',
                                paddingTop: '14px',
                                paddingBottom: '14px',
                            },
                        }}
                        className='h4'
                    >
                        Webseite
                    </Tab>
                    {enableLocations && (
                        <Tab
                            value="einrichtungen"
                            css={{
                                '@media (max-width: 767px)': {
                                    width: '100%',
                                    paddingTop: '14px',
                                    paddingBottom: '14px',
                                },
                            }}
                            className='h4'
                        >
                            Einrichtungen
                        </Tab>
                    )}
                    {(enableContact || enableContactPerson) && (
                        <Tab
                            value="mitarbeiter"
                            css={{
                                '@media (max-width: 767px)': {
                                    width: '100%',
                                    height: '100%',
                                    paddingTop: '14px',
                                    paddingBottom: '14px',
                                },
                            }}
                            className='h4'
                        >
                            Ärzte und Mitarbeiter
                        </Tab>
                    )}
                    {enableBlogPosts && (
                        <Tab
                            value="press"
                            css={{
                                '@media (max-width: 767px)': {
                                    width: '100%',
                                    height: '100%',
                                    paddingTop: '14px',
                                    paddingBottom: '14px',
                                },
                            }}
                            className='h4'
                        >
                            Pressemeldungen
                        </Tab>
                    )}
                </TabList>
                <TabPanels>
                    <TabPanel value="webseiten">
                        <InstantSearch
                            indexName={`${searchPrefix}searchable_posts`}
                            {...algoliaProps}
                        >
                            <HiddenSearchBox searchTerm={searchTerm} />
                            <SearchTileHits />
                            <div css={{
                                borderTop: '1px solid #D5D5D5',
                                marginBottom: '4px',
                                marginTop: '103px',
                                '@media (max-width: 767px)': {
                                    marginTop: '24px',
                                },
                            }}
                            />
                            <Pagination />
                        </InstantSearch>
                    </TabPanel>
                    {enableLocations && (
                        <TabPanel value="einrichtungen">
                            <InstantSearch
                                indexName={`${searchPrefix}posts_einrichtung`}
                                {...algoliaProps}
                            >
                                <HiddenSearchBox searchTerm={searchTerm} />
                                <SearchTileFacilityHits />
                                <div css={{
                                    borderTop: '1px solid #D5D5D5',
                                    marginBottom: '4px',
                                    marginTop: '103px',
                                    '@media (max-width: 767px)': {
                                        marginTop: '24px',
                                    },
                                }}
                                />
                                <Pagination />
                            </InstantSearch>
                        </TabPanel>
                    )}
                    {(enableContact || enableContactPerson) && (
                        <TabPanel value="mitarbeiter">
                            <InstantSearch
                                indexName={enableContactPerson ? `${searchPrefix}posts_contact_person` : `${searchPrefix}posts_kontakt`}
                                {...algoliaProps}
                            >
                                <HiddenSearchBox searchTerm={searchTerm} />
                                <Persons />
                                <div css={{
                                    borderTop: '1px solid #D5D5D5',
                                    marginBottom: '4px',
                                    marginTop: '103px',
                                    '@media (max-width: 767px)': {
                                        marginTop: '24px',
                                    },
                                }}
                                />
                                <Pagination />
                            </InstantSearch>
                        </TabPanel>
                    )}
                    {enableBlogPosts && (
                        <TabPanel value="press">
                            <InstantSearch
                                indexName={`${searchPrefix}posts_post`}
                                {...algoliaProps}
                            >
                                <HiddenSearchBox searchTerm={searchTerm} />
                                <SearchTilePressHits />
                                <div css={{
                                    borderTop: '1px solid #D5D5D5',
                                    marginBottom: '4px',
                                    marginTop: '103px',
                                    '@media (max-width: 767px)': {
                                        marginTop: '24px',
                                    },
                                }}
                                />
                                <Pagination />
                            </InstantSearch>
                        </TabPanel>
                    )}
                </TabPanels>
            </Tabs>
        </div>
    );
};

export default SearchPage;
